import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlertTriangle, ArrowLeft, Check, ChevronDown, ChevronUp, Copy, Download, ExternalLink, FileText, RefreshCw, Search, Send, Share2, Trash2, UserMinus, X } from 'lucide-react';
import api from '../../../utils/authUtils';
import { mediaUrl } from '../../../utils/mediaUrl';
import ShareEngagementModal from '../../speaker/ShareEngagementModal';
import InviteModal from './InviteModal';
import FieldDiffPicker, { defaultResolutions, FIELD_LABELS } from '../../FieldDiffPicker';
import InterestPicker from '../../InterestPicker';
import { STATUSES } from '../../../constants/doctorProfile';
import {
  Container, BackLink, Header, Title, Card, CardTitle, FormGrid, Field, WideField, Input, Textarea, HintNote,
  Checks, CheckLabel, Actions, Button, Message, SourceBox, SourceLink, Photo, Empty, LinkRow, Usage,
  EngList, EngRow, EngRowTop, EngName, EngMeta, EngTag, PendingTag, SlidesTag, ConsentTag, EngBody, WORKSPACE,
  List, ListItem, HistList, HistRow,
} from './ui';

const API_URL = process.env.REACT_APP_API_URL || '';

const EMPTY = {
  fullName:'', email:'', title:'', specialty:'', institution:'', city:'', country:'', shortBioDe:'', shortBioEn:'',
  motivationDe:'', motivationEn:'',
  photoUrl:'', linkedInUrl:'', facebookUrl:'', instagramUrl:'', websiteUrl:'',
  professionalStatuses:['SPEAKER'], interests:[]
};
// The `hint` flag marks exactly the fields SpeakerEnrichmentWorker.researchRequest
// actually sends to the AI as disambiguating context (see OpenAiSpeakerEnrichmentClient.
// prompt's "hint" lines) - specialty and the photo/social links are researched
// *outputs*, never inputs, so they don't get the note even though they share
// this same generic field loop.
const fields = [
  ['title','speaker.field.title',true],['specialty','speaker.field.specialty',false],['institution','speaker.field.institution',true],
  ['city','speaker.field.city',true],['country','speaker.field.country',true],['photoUrl','speaker.field.photoUrl',false],['linkedInUrl','speaker.field.linkedin',false],
  ['facebookUrl','speaker.field.facebook',false],['instagramUrl','speaker.field.instagram',false],['websiteUrl','speaker.field.website',false]
];

const Sources = ({items}) => (
  <SourceBox>
    <strong><FormattedMessage id="speakerMgmt.source" defaultMessage="Source"/></strong>
    {items?.length ? items.map((source,index) => source.url ? (
      <SourceLink key={index} href={source.url} target="_blank" rel="noreferrer">
        <ExternalLink size={12}/>{source.title || source.url}
        {Number.isFinite(source.confidence) ? ` (${Math.round(source.confidence*100)}%)` : ''}
      </SourceLink>
    ) : <div key={index}>{source.title || <FormattedMessage id="speakerMgmt.organizerInput" defaultMessage="Organizer input"/>}</div>) : <div><FormattedMessage id="speakerMgmt.noSource" defaultMessage="No source recorded"/></div>}
  </SourceBox>
);

const profilePayload = profile => ({
  ...EMPTY, ...profile,
  professionalStatuses: profile.professionalStatuses || [],
  interests: profile.interests || []
});

// One event engagement row in the speaker's Events card. A speaker has one
// profile but many engagements - talk-title/slides/consent all target this
// row's event via its event id, while uninviting targets this one row via its
// own engagement id (a person can hold several engagements for one event).
const EngagementRow = ({ profileId, engagement, t, intl, onChanged, notify, busy, setBusy, onShare }) => {
  const [open, setOpen] = useState(false);
  const [titleEdit, setTitleEdit] = useState(engagement.talkTitle || '');
  const [history, setHistory] = useState(null);
  useEffect(() => { setTitleEdit(engagement.talkTitle || ''); }, [engagement.talkTitle]); // eslint-disable-line react-hooks/exhaustive-deps
  // The materials trail is fetched only when the row is actually opened: it is
  // an audit record (see MaterialsAuditLogEntry), not something to pay a
  // request per row for on a page listing every speaker.
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    api.get(`/api/cms/speaker-profiles/${profileId}/engagement/${engagement.eventId}/materials-history`)
      .then(({ data }) => { if (live) setHistory(data || []); })
      .catch(() => { if (live) setHistory([]); });
    return () => { live = false; };
  }, [open, profileId, engagement.eventId]);
  const statusLabel = s => t(`speaker.engagement.${s}`, s);
  const dateLabel = v => v ? new Date(v).toLocaleDateString(intl.locale, { year: 'numeric', month: 'short', day: 'numeric' }) : '';
  const saveTitle = async () => {
    setBusy(true);
    try {
      await api.put(`/api/cms/speaker-profiles/${profileId}/engagement/${engagement.eventId}/talk-title`, { title: titleEdit });
      await onChanged();
      notify({ text: t('speakerMgmt.talk.saved', 'Talk title saved.') });
    } catch (err) { notify({ error: true, text: err.response?.data || t('speakerMgmt.talk.saveError', 'Could not save the talk title.') }); }
    finally { setBusy(false); }
  };
  const acceptSuggestion = async () => {
    setBusy(true);
    try {
      await api.post(`/api/cms/speaker-profiles/${profileId}/engagement/${engagement.eventId}/talk-title/accept`);
      await onChanged();
      notify({ text: t('speakerMgmt.talk.accepted', 'Title change accepted.') });
    } catch (err) { notify({ error: true, text: err.response?.data || t('speakerMgmt.talk.acceptError', 'Could not accept the title change.') }); }
    finally { setBusy(false); }
  };
  const dismissSuggestion = async () => {
    setBusy(true);
    try {
      await api.delete(`/api/cms/speaker-profiles/${profileId}/engagement/${engagement.eventId}/talk-title/suggestion`);
      await onChanged();
      notify({ text: t('speakerMgmt.talk.dismissed', 'Title change dismissed.') });
    } catch (err) { notify({ error: true, text: err.response?.data || t('speakerMgmt.talk.dismissError', 'Could not dismiss the title change.') }); }
    finally { setBusy(false); }
  };
  const roleLabel = r => t(`speakerMgmt.confirm.role.${r}`, r.charAt(0)+r.slice(1).toLowerCase());
  const sessionMeta = [engagement.sessionTitle, [engagement.sessionStartTime, engagement.sessionEndTime].filter(Boolean).join('–')].filter(Boolean).join(' · ');
  // Undoes exactly this one engagement (role/session) - not the person's
  // whole relation to the event. They may keep every other role at the same
  // event, so the row is the unit of removal and is identified by its own id
  // (the backend refuses an engagement that isn't this profile's). No
  // notification is sent. See SpeakerProfileService.removeEngagement.
  const uninvite = async () => {
    const what = [roleLabel(engagement.role), engagement.eventName, sessionMeta].filter(Boolean).join(' · ');
    if(!window.confirm(t('speakerMgmt.uninvite.confirm','Uninvite {what}? Only this engagement is removed; any other roles this person holds are kept.',{what}))) return;
    setBusy(true);
    try {
      await api.delete(`/api/cms/speaker-profiles/${profileId}/engagements/${engagement.id}`);
      await onChanged();
      notify({ text: t('speakerMgmt.uninvite.done','Engagement removed.') });
    } catch (err) { notify({ error: true, text: err.response?.data || t('speakerMgmt.uninvite.error','Could not remove this engagement.') }); }
    finally { setBusy(false); }
  };
  const uninviteButton = (
    <Button danger disabled={busy} onClick={uninvite}><UserMinus size={14}/><FormattedMessage id="speakerMgmt.uninvite.action" defaultMessage="Uninvite"/></Button>
  );
  // Lets the admin preview/download the exact share-card image this
  // engagement's speaker would generate for themselves (ShareEngagementModal,
  // also used by SpeakerDashboard.js and AttendeeProfile.js) - only makes
  // sense once the engagement is actually confirmed, same gate those surfaces
  // use.
  const shareButton = engagement.status === 'CONFIRMED' && (
    <Button secondary disabled={busy} onClick={(e) => { e.stopPropagation(); onShare(engagement); }}>
      <Share2 size={14}/><FormattedMessage id="speakerMgmt.share.action" defaultMessage="Preview share image"/>
    </Button>
  );
  // MODERATOR/PANELIST rows have no talk/slides/materials of their own to
  // manage - only SPEAKER does (see docs/speaker-session-roles.md) - so they
  // render as a simple read-only summary rather than an expandable editing
  // body. The row isn't clickable, so the uninvite action sits directly in it.
  if (engagement.role !== 'SPEAKER') {
    return (
      <EngRow>
        <EngRowTop as="div">
          <EngName>{engagement.eventName}</EngName>
          <EngMeta>{[dateLabel(engagement.eventDateTime), sessionMeta, engagement.eventLocation].filter(Boolean).join(' · ')}</EngMeta>
          <EngTag $confirmed={engagement.status === 'CONFIRMED'}>{roleLabel(engagement.role)}</EngTag>
          <EngTag $confirmed={engagement.status === 'CONFIRMED'}>{statusLabel(engagement.status)}</EngTag>
          {shareButton}
          {uninviteButton}
        </EngRowTop>
      </EngRow>
    );
  }
  const slidesPending = engagement.slidesUrl && !engagement.slidesAcknowledgedAt;
  const acknowledgeSlides = async () => {
    setBusy(true);
    try {
      await api.post(`/api/cms/speaker-profiles/${profileId}/engagement/${engagement.eventId}/slides/acknowledge`);
      await onChanged();
      notify({ text: t('speakerMgmt.talk.slidesAcknowledged', 'Slides marked as reviewed.') });
    } catch (err) { notify({ error: true, text: err.response?.data || t('speakerMgmt.talk.slidesAcknowledgeError', 'Could not mark the slides as reviewed.') }); }
    finally { setBusy(false); }
  };
  const consent = engagement.materialsPublishConsent || 'UNSET';
  const consentLabel = { GRANTED: t('speakerMgmt.talk.consentGranted', 'Consent given'), DENIED: t('speakerMgmt.talk.consentDenied', 'Consent declined'), UNSET: t('speakerMgmt.talk.consentUnset', 'Not answered yet') }[consent];
  return (
    <EngRow>
      <EngRowTop type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <EngName>{engagement.eventName}</EngName>
        <EngMeta>{[dateLabel(engagement.eventDateTime), sessionMeta || engagement.eventLocation].filter(Boolean).join(' · ')}</EngMeta>
        <EngTag $confirmed={engagement.status === 'CONFIRMED'}>{roleLabel(engagement.role)}</EngTag>
        <EngTag $confirmed={engagement.status === 'CONFIRMED'}>{statusLabel(engagement.status)}</EngTag>
        {engagement.slidesUrl && <SlidesTag title={t('speakerMgmt.talk.slidesUploadedHint', 'Slides uploaded')}><FileText size={12} /><FormattedMessage id="speakerMgmt.talk.slidesTag" defaultMessage="Slides" /></SlidesTag>}
        {slidesPending && <PendingTag><FormattedMessage id="speakerMgmt.talk.slidesPending" defaultMessage="New slides to review" /></PendingTag>}
        {engagement.slidesUrl && <ConsentTag $tone={consent==='GRANTED'?'granted':consent==='DENIED'?'denied':'unset'}>{consentLabel}</ConsentTag>}
        {engagement.materialsPublished && <ConsentTag $tone="granted"><FormattedMessage id="speakerMgmt.talk.materialsPublished" defaultMessage="Published" /></ConsentTag>}
        {engagement.talkTitleSuggestion && <PendingTag><FormattedMessage id="speakerMgmt.talk.suggestionPending" defaultMessage="Title change requested" /></PendingTag>}
        {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
      </EngRowTop>
      {open && (
        <EngBody>
          {engagement.talkTitleSuggestion && <Message warn><strong><FormattedMessage id="speakerMgmt.talk.suggestedBy" defaultMessage="Suggested:"/></strong> {engagement.talkTitleSuggestion}<Actions style={{marginTop:'.5rem'}}><Button disabled={busy} onClick={acceptSuggestion}><Check size={14}/><FormattedMessage id="speakerMgmt.talk.accept" defaultMessage="Accept"/></Button><Button secondary disabled={busy} onClick={dismissSuggestion}><X size={14}/><FormattedMessage id="speakerMgmt.talk.dismiss" defaultMessage="Dismiss"/></Button></Actions></Message>}
          <Field><FormattedMessage id="speakerMgmt.talk.title" defaultMessage="Talk title"/><Input value={titleEdit} onChange={e=>setTitleEdit(e.target.value)}/></Field>
          <Actions><Button secondary disabled={busy} onClick={saveTitle}><Check size={16}/><FormattedMessage id="speakerMgmt.talk.save" defaultMessage="Save title"/></Button></Actions>
          <Field style={{marginTop:'.8rem'}}>
            <FormattedMessage id="speakerMgmt.talk.slides" defaultMessage="Slides"/>
            {engagement.slidesUrl
              ? <LinkRow>
                  <FileText size={15}/><span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{engagement.slidesFilename || engagement.slidesUrl}</span>
                  <Button as="a" href={`${API_URL}${engagement.slidesUrl}`} target="_blank" rel="noreferrer" download={engagement.slidesFilename || undefined} secondary>
                    <Download size={14}/><FormattedMessage id="speakerMgmt.talk.slidesDownload" defaultMessage="Download"/>
                  </Button>
                  {slidesPending && <Button disabled={busy} onClick={acknowledgeSlides}><Check size={14}/><FormattedMessage id="speakerMgmt.talk.slidesAcknowledge" defaultMessage="Mark as reviewed"/></Button>}
                  {/* No publish action for the admin - publishing is covered by
                      the speaker's own consent (see SpeakerEngagementDetails).
                      The admin only needs to know that we are allowed to use
                      these materials, shown below when consent was granted. */}
                  {consent === 'GRANTED' && <ConsentTag $tone="granted"><FormattedMessage id="speakerMgmt.talk.mayPublish" defaultMessage="May be published"/></ConsentTag>}
                </LinkRow>
              : <span style={{fontWeight:400,color:'#94a3b8'}}><FormattedMessage id="speakerMgmt.talk.slidesNone" defaultMessage="No slides uploaded yet."/></span>}
          </Field>
          <Field style={{marginTop:'.6rem'}}>
            <FormattedMessage id="speakerMgmt.talk.consentLabel" defaultMessage="Publish consent"/>
            <span style={{fontWeight:400,color:'#334155'}}>{consentLabel}</span>
          </Field>
          {/* Consent is stored as a single current value that a later answer
              overwrites - this is the only place an earlier answer, and a
              publish window a withdrawal has since cleared, is still visible. */}
          {history?.length > 0 && (
            <Field style={{marginTop:'.6rem'}}>
              <FormattedMessage id="speakerMgmt.talk.historyLabel" defaultMessage="Materials history"/>
              <HistList>
                {history.map((entry, index) => (
                  <HistRow key={index}>
                    <span>{entry.occurredAt ? new Date(entry.occurredAt).toLocaleString(intl.locale, { year:'numeric', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' }) : ''}</span>
                    <ConsentTag $tone={entry.action === 'CONSENT_GRANTED' || entry.action === 'MATERIALS_PUBLISHED' ? 'granted' : entry.action === 'CONSENT_DENIED' ? 'denied' : 'unset'}>
                      {t(`speakerMgmt.talk.history.${entry.action}`, entry.action)}
                    </ConsentTag>
                  </HistRow>
                ))}
              </HistList>
            </Field>
          )}
          <Actions>{shareButton}{uninviteButton}</Actions>
        </EngBody>
      )}
    </EngRow>
  );
};

const SpeakerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id,def,values) => intl.formatMessage({id,defaultMessage:def},values);
  const roleLabel = s => t(`speaker.role.${s}`, s.replaceAll('_',' '));
  const [selected,setSelected] = useState(null);
  const [form,setForm] = useState(EMPTY);
  const [events,setEvents] = useState([]);
  const [busy,setBusy] = useState(false);
  const [dirty,setDirty] = useState(false);
  const [autoSaving,setAutoSaving] = useState(false);
  const [message,setMessage] = useState(null);
  const [inviteLink,setInviteLink] = useState('');
  const [confirming,setConfirming] = useState(false);
  const [researchResolutions,setResearchResolutions] = useState({});
  const [pendingReview,setPendingReview] = useState(null);
  const [shareEngagement,setShareEngagement] = useState(null);

  useEffect(()=>{
    api.get('/api/cms/event/options').then(({data})=>setEvents(data||[])).catch(()=>{});
  },[]);
  // `selected`/`form` show a staged draft's working value for a field
  // actually pending, not the live account (see SpeakerProfileMapper.
  // adminDto) - fetch the diff against the real account separately so the
  // admin can confirm what actually changed (see
  // SpeakerProfileService.adminPendingReview). Called after every load AND
  // every auto-save, since a plain edit now stages just like an invite or a
  // resolved research run - the admin should see it land here immediately,
  // not only after the next full reload.
  const refreshPendingReview = (data) => {
    if (data.accountId) {
      api.get(`/api/cms/speaker-profiles/${id}/pending-review`)
        .then(({data:review})=>setPendingReview(review)).catch(()=>setPendingReview(null));
    } else {
      setPendingReview(null);
    }
  };
  const load = async () => {
    try {
      const {data}=await api.get(`/api/cms/speaker-profiles/${id}`);
      setSelected(data);setForm(profilePayload(data));setDirty(false);
      refreshPendingReview(data);
    } catch(err) {
      if(err.response?.status===401) navigate('/admin/login');
      else if(err.response?.status===404) setMessage({error:true,text:t('speakerMgmt.detail.notFound','This speaker profile no longer exists.')});
      else setMessage({error:true,text:t('speakerMgmt.msg.loadFailed','Could not load speaker drafts.')});
    }
  };
  useEffect(()=>{load();},[id]); // eslint-disable-line react-hooks/exhaustive-deps
  // While research is in flight, poll for its status/pendingResearch diffs so
  // the "New research results to review" card appears once it lands - but
  // never touch `form`. An admin may be mid-edit on any field while the job
  // is still PENDING/RUNNING - a full load() here would silently overwrite
  // that unsaved text with stale server data every 4 seconds until the job
  // finished.
  useEffect(()=>{
    if(!selected||!['PENDING','RUNNING'].includes(selected.enrichmentStatus)) return undefined;
    const timer=setInterval(async () => {
      try { const {data}=await api.get(`/api/cms/speaker-profiles/${id}`); setSelected(data); }
      catch(err) { /* transient poll failure - next tick will retry */ }
    },4000);
    return ()=>clearInterval(timer);
  },[selected]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{
    if(selected?.pendingResearch?.length) setResearchResolutions(defaultResolutions(selected.pendingResearch));
  },[selected?.enrichmentJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const change = (key,value) => { setForm(current=>({...current,[key]:value})); setDirty(true); };
  const toggleItem = (key,value) => { setForm(current=>{const cur=current[key]||[];return {...current,[key]:cur.includes(value)?cur.filter(v=>v!==value):[...cur,value]};}); setDirty(true); };
  const save = async () => {
    setAutoSaving(true);setMessage(null);
    try {const {data}=await api.put(`/api/cms/speaker-profiles/${id}`,form);setSelected(data);setForm(profilePayload(data));setDirty(false);refreshPendingReview(data);}
    catch(err){setMessage({error:true,text:err.response?.data || t('speakerMgmt.msg.saveFailed','Could not save draft.')});}
    finally{setAutoSaving(false);}
  };
  // Manual edits save themselves - no "Save draft" click required (see the
  // status text next to the actions below). Debounced so a fast typist
  // doesn't fire a PUT per keystroke; skipped while busy with something
  // else (research/invite) so it can't race those requests' own save of
  // the same form - the effect just re-fires once busy clears.
  useEffect(() => {
    if (!dirty || busy) return undefined;
    const timer = setTimeout(save, 1200);
    return () => clearTimeout(timer);
  }, [form, busy]); // eslint-disable-line react-hooks/exhaustive-deps
  const retry = async () => {
    setBusy(true);try{await api.post(`/api/cms/speaker-profiles/${id}/retry`);await load();}
    catch(err){setMessage({error:true,text:err.response?.data || t('speakerMgmt.msg.retryFailed','Could not retry research.')});}finally{setBusy(false);}
  };
  // Sending an invite never touches this email's existing, live account
  // data (or unclaimed history) directly - the speaker still decides, field
  // by field, at their own accept-time review (see
  // SpeakerProfileService.pendingProfileReview/resolvePendingProfileReview)
  // before anything reaches their real Account. There's deliberately no
  // extra invite-time review step for the admin any more: the draft is kept
  // continuously in sync with the account holder's own edits, even before
  // any invite is sent (AttendeeAccountService.mirrorOntoPendingSpeakerDraft
  // now matches a not-yet-invited candidate by email, not just an already-
  // linked one) - last write wins, so whatever's on the draft here already
  // reflects that. A separate "here's what will change" gate would just be
  // re-litigating a decision this sync already made in real time.
  const confirmInvite = async (eventId, assignments) => {
    setBusy(true);setMessage(null);setInviteLink('');
    try {
      await api.put(`/api/cms/speaker-profiles/${id}`,form);
      await sendInvite(eventId,assignments);
    } catch(err){setMessage({error:true,text:err.response?.data || intl.formatMessage({id:'speakerMgmt.invite.error'})});}
    finally{setBusy(false);}
  };
  const sendInvite = async (eventId, assignments) => {
    const {data}=await api.post(`/api/cms/speaker-profiles/${id}/approve-and-invite`,{profile:form,eventId,assignments});
    if(data.emailSent) setMessage({text:intl.formatMessage({id:'speakerMgmt.invite.sent'},{email:form.email})});
    else {
      setInviteLink(`${window.location.origin}/speaker/invite?token=${data.inviteToken}`);
      setMessage({error:true,text:intl.formatMessage({id:'speakerMgmt.invite.emailFailed'})});
    }
    setConfirming(false);
    await load();
  };
  const resolveResearch = async () => {
    setBusy(true);setMessage(null);
    try {
      // Save whatever's currently in the manual fields (e.g. motivationDe/En,
      // which the AI never proposes) first - otherwise the load() below
      // overwrites unsaved edits with stale server data, same risk the
      // polling effect above already guards against.
      await api.put(`/api/cms/speaker-profiles/${id}`,form);
      await api.post(`/api/cms/speaker-profiles/${id}/enrichment-jobs/${selected.enrichmentJobId}/resolve`,researchResolutions);
      await load();
      setMessage({text:t('speakerMgmt.diff.applied','Research results applied.')});
    } catch(err){setMessage({error:true,text:err.response?.data || t('speakerMgmt.diff.applyError','Could not apply the research results.')});}
    finally{setBusy(false);}
  };
  const remove = async () => {
    if(!window.confirm(t('speakerMgmt.confirmDelete','Remove the profile draft for {email}?',{email:selected.email||selected.fullName}))) return;
    await api.delete(`/api/cms/speaker-profiles/${id}`);navigate(WORKSPACE);
  };

  if (!selected) return <Container>{message&&<Message error={message.error}>{message.text}</Message>}</Container>;

  const hasAccount = !!selected.accountId;

  return <Container>
    <BackLink to={WORKSPACE}><ArrowLeft size={16}/><FormattedMessage id="speakerMgmt.backList" defaultMessage="Back to speakers"/></BackLink>
    <Header><Title>{selected.fullName}</Title><Button secondary onClick={load}><RefreshCw size={16}/><FormattedMessage id="speakerMgmt.refresh" defaultMessage="Refresh"/></Button></Header>
    {message&&<Message error={message.error}>{message.text}</Message>}
    {inviteLink&&<Card><LinkRow><code>{inviteLink}</code><Button onClick={()=>navigator.clipboard.writeText(inviteLink)}><Copy size={15}/><FormattedMessage id="speakerMgmt.copy" defaultMessage="Copy"/></Button></LinkRow></Card>}
    {!selected.enrichmentJobId && (
      <Card>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'1rem',flexWrap:'wrap'}}>
          <div style={{display:'flex',alignItems:'center',gap:'.6rem'}}>
            <Search size={18} color="#0f4c68"/>
            <span><FormattedMessage id="speakerMgmt.detail.neverResearched" defaultMessage="AI research hasn't been started for this speaker yet."/></span>
          </div>
          <Button disabled={busy} onClick={retry}><Search size={16}/><FormattedMessage id="speakerMgmt.detail.startResearch" defaultMessage="Start AI research"/></Button>
        </div>
      </Card>
    )}
    {['PENDING','RUNNING'].includes(selected.enrichmentStatus) && (
      <Message>
        <RefreshCw size={14} style={{verticalAlign:'-2px',marginRight:'.35rem'}}/>
        <FormattedMessage id="speakerMgmt.detail.researching" defaultMessage="AI research is running — this page updates automatically once it's done." />
      </Message>
    )}
    {selected.duplicateEmailProfileId && (
      <Message warn>
        <AlertTriangle size={14} style={{verticalAlign:'-2px',marginRight:'.35rem'}}/>
        <FormattedMessage id="speakerMgmt.detail.duplicateEmail" defaultMessage="This email is also used by another profile: {name}. Inviting one of them will fail until this is resolved (rename, fix the email, or delete the duplicate)."
          values={{name:<Link to={`${WORKSPACE}/${selected.duplicateEmailProfileId}`}><strong>{selected.duplicateEmailProfileName}</strong></Link>}}/>
      </Message>
    )}
    {selected.detectedAssignments?.length>0 && (
      <Message>
        <FormattedMessage id="speakerMgmt.detail.detectedAssignments" defaultMessage="Detected from a program document{event}:"
          values={{event: selected.detectedEventName ? <> ({selected.detectedEventName})</> : ''}}/>
        <ul style={{margin:'.4rem 0 0',paddingLeft:'1.1rem'}}>
          {selected.detectedAssignments.map((a,i)=>(
            <li key={i}>
              <strong>{t(`speakerMgmt.confirm.role.${a.role}`,a.role)}</strong>
              {a.sessionTitle ? <> — {a.sessionTitle}</> : ''}
              {a.talkTitle ? <> · {a.talkTitle}</> : ''}
            </li>
          ))}
        </ul>
      </Message>
    )}
    {selected.pendingResearch?.length>0 && (
      <Card>
        <CardTitle><FormattedMessage id="speakerMgmt.diff.title" defaultMessage="New research results to review"/></CardTitle>
        <FieldDiffPicker diffs={selected.pendingResearch} resolutions={researchResolutions} onChange={setResearchResolutions}/>
        {hasAccount && (
          <Message style={{marginTop:'.75rem'}}>
            <FormattedMessage id="speakerMgmt.diff.stagedNote" defaultMessage="This speaker already has an account - applying will stage these changes for their own review, not update their account immediately."/>
          </Message>
        )}
        <Actions>
          <Button disabled={busy} onClick={resolveResearch}><Check size={16}/><FormattedMessage id="speakerMgmt.diff.apply" defaultMessage="Apply selected values"/></Button>
        </Actions>
      </Card>
    )}
    {pendingReview?.diffs?.length>0 && (
      <Card>
        <CardTitle><FormattedMessage id="speakerMgmt.pendingReview.title" defaultMessage="Pending the speaker's own review"/></CardTitle>
        <Message><FormattedMessage id="speakerMgmt.pendingReview.intro" defaultMessage="These changes are staged - from an edit, a sent invite, or applied research - but nothing here is live yet. The speaker sees them the next time they accept an invitation (new or resent), not on an ordinary login. A field that was blank fills in automatically then; a field that already had a value is offered to them as an opt-in choice, kept unless they pick the new one."/></Message>
        <List style={{marginTop:'.6rem'}}>
          {pendingReview.diffs.map(d => (
            <ListItem key={d.field} style={{display:'block'}}>
              <strong>{t(FIELD_LABELS[d.field] || d.field, d.field)}</strong>
              <div style={{fontSize:'.85rem',color:'#334155',marginTop:'.25rem'}}>
                {d.currentValue || <FormattedMessage id="speakerMgmt.diff.blank" defaultMessage="(blank)"/>}
                {' → '}
                {d.proposedValue}
              </div>
            </ListItem>
          ))}
        </List>
      </Card>
    )}
    <Card><CardTitle><FormattedMessage id="speakerMgmt.review" defaultMessage="Review profile"/></CardTitle>
      {selected.enrichmentError&&<Message error>{selected.enrichmentError}</Message>}
      <Usage>
        <span><strong><FormattedMessage id="speakerMgmt.usage.attempts" defaultMessage="Attempts:"/></strong> {selected.enrichmentAttempts ?? 0}</span>
        <span><strong><FormattedMessage id="speakerMgmt.usage.webSearches" defaultMessage="Web searches:"/></strong> {selected.enrichmentWebSearchCalls ?? '—'}</span>
        <span><strong><FormattedMessage id="speakerMgmt.usage.tokens" defaultMessage="Tokens:"/></strong> <FormattedMessage id="speakerMgmt.usage.tokensValue" defaultMessage="{total} ({in} in / {out} out)" values={{total:selected.enrichmentTotalTokens?.toLocaleString() ?? '—',in:selected.enrichmentInputTokens?.toLocaleString() ?? '—',out:selected.enrichmentOutputTokens?.toLocaleString() ?? '—'}}/></span>
        <span><strong><FormattedMessage id="speakerMgmt.usage.cost" defaultMessage="Estimated cost:"/></strong> {selected.enrichmentEstimatedCostUsd == null ? '—' : `$${Number(selected.enrichmentEstimatedCostUsd).toFixed(4)}`}</span>
      </Usage>
      <FormGrid>
        {/* Unlike email below, a name correction here is just an ordinary
            staged field - updateAdmin already routes it through the same
            stagingUserFor/applyToUser path as every other field (title,
            institution, bio, ...), so it never touches the live account
            directly and the speaker still reviews it like anything else.
            Nothing about name affects resolveAccountForInvite's account
            resolution, so there's no reason to lock it once an account
            exists. */}
        <Field><span><FormattedMessage id="speaker.field.name" defaultMessage="Name"/></span><Input value={form.fullName||''} onChange={e=>change('fullName',e.target.value)}/><Sources items={selected.sources?.fullName}/></Field>
        {/* Locked exactly when selected.emailEditable is false - server-computed
            (SpeakerProfileMapper.emailMatchesRealIdentity): true once this email
            resolves to a real Account or unclaimed User, whether that's a live
            login or a profile matched to an existing identity by name at import
            time (SpeakerProfileService.createDraftForExistingIdentity). Changing
            it then risks resolveAccountForInvite finding (or creating) the wrong
            account. A profile whose email was never matched to anyone real - just
            a first guess with nothing real behind it - stays open to correction. */}
        <Field><span><FormattedMessage id="speaker.field.email" defaultMessage="Email"/>{!selected.emailEditable?<> 🔒</>:<> *</>}</span><Input type="email" disabled={!selected.emailEditable} value={form.email||''} onChange={e=>change('email',e.target.value)} placeholder={t(['PENDING','RUNNING'].includes(selected.enrichmentStatus)?'speakerMgmt.emailPending':'speakerMgmt.emailNotResearched','Email pending research')}/><Sources items={selected.sources?.email}/></Field>
        {fields.map(([key,label,hint])=><Field key={key}><FormattedMessage id={label}/>{hint&&<> <HintNote><FormattedMessage id="speakerMgmt.field.aiHint" defaultMessage="(used as an AI research hint)"/></HintNote></>}<Input value={form[key]||''} onChange={e=>change(key,e.target.value)}/><Sources items={selected.sources?.[key]}/></Field>)}
        {form.photoUrl&&<WideField><FormattedMessage id="speakerMgmt.imagePreview" defaultMessage="Image preview"/><Photo src={mediaUrl(form.photoUrl)} alt="" referrerPolicy="no-referrer"/><Sources items={selected.sources?.photoUrl}/></WideField>}
        <WideField><FormattedMessage id="speaker.field.shortBioDe" defaultMessage="Short bio (German)"/><Textarea value={form.shortBioDe||''} onChange={e=>change('shortBioDe',e.target.value)}/><Sources items={selected.sources?.shortBioDe}/></WideField>
        <WideField><FormattedMessage id="speaker.field.shortBioEn" defaultMessage="Short bio (English)"/><Textarea value={form.shortBioEn||''} onChange={e=>change('shortBioEn',e.target.value)}/><Sources items={selected.sources?.shortBioEn}/></WideField>
        <WideField><FormattedMessage id="speaker.field.motivationDe" defaultMessage="I am part of HEARTS - DEMOCRATIZING SCIENCE - because (German)"/><Textarea value={form.motivationDe||''} onChange={e=>change('motivationDe',e.target.value)}/><Sources items={selected.sources?.motivationDe}/></WideField>
        <WideField><FormattedMessage id="speaker.field.motivationEn" defaultMessage="I am part of HEARTS - DEMOCRATIZING SCIENCE - because (English)"/><Textarea value={form.motivationEn||''} onChange={e=>change('motivationEn',e.target.value)}/><Sources items={selected.sources?.motivationEn}/></WideField>
        <WideField><FormattedMessage id="speakerMgmt.statusCsv" defaultMessage="Roles / status"/><Checks>{STATUSES.map(status=><CheckLabel key={status}><input type="checkbox" checked={(form.professionalStatuses||[]).includes(status)} onChange={()=>toggleItem('professionalStatuses',status)}/>{roleLabel(status)}</CheckLabel>)}</Checks><Sources items={selected.sources?.professionalStatuses}/></WideField>
        <WideField><FormattedMessage id="speakerMgmt.interestsCsv" defaultMessage="Cardiac interests"/><InterestPicker compact value={form.interests} onChange={v=>change('interests',v)}/><Sources items={selected.sources?.interests}/></WideField>
      </FormGrid>
      <Card>
        <CardTitle><FormattedMessage id="speakerMgmt.events" defaultMessage="Events"/></CardTitle>
        {(selected.engagements||[]).length===0
          ? <Empty><FormattedMessage id="speakerMgmt.events.empty" defaultMessage="No event invitations yet for this speaker."/></Empty>
          : <EngList>{(selected.engagements||[]).map((e,i)=>(
              <EngagementRow key={`${e.eventId}-${e.role}-${e.sessionTitle||''}-${i}`} profileId={selected.id} engagement={e} t={t} intl={intl}
                onChanged={load} notify={setMessage} busy={busy} setBusy={setBusy} onShare={setShareEngagement}/>
            ))}</EngList>}
      </Card>
      <Actions>
        {/* Manual edits auto-save (see the debounce effect above) - this is
            just status, not an action; disabled so it's not mistaken for
            a button that does something different from what's already
            happening. */}
        <Button secondary disabled style={{cursor:'default',opacity:1}}>
          <Check size={16}/>
          {autoSaving
            ? <FormattedMessage id="speakerMgmt.autoSaving" defaultMessage="Saving…"/>
            : dirty
              ? <FormattedMessage id="speakerMgmt.autoSavePending" defaultMessage="Unsaved changes…"/>
              : <FormattedMessage id="speakerMgmt.autoSaved" defaultMessage="All changes saved"/>}
        </Button>
        <Button secondary disabled={busy} onClick={retry}><RefreshCw size={16}/><FormattedMessage id="speakerMgmt.retry" defaultMessage="Retry research"/></Button>
        <Button disabled={busy||['PENDING','RUNNING'].includes(selected.enrichmentStatus)} onClick={()=>setConfirming(true)}>
          <Send size={16}/>{hasAccount
            ? <FormattedMessage id="speakerMgmt.detail.inviteAnother" defaultMessage="Invite to another event"/>
            : <FormattedMessage id="speakerMgmt.approve" defaultMessage="Approve & send invite"/>}
        </Button>
        <Button danger disabled={busy} onClick={remove}><Trash2 size={16}/><FormattedMessage id="speakerMgmt.delete" defaultMessage="Delete"/></Button>
      </Actions>
    </Card>

    <InviteModal open={confirming} onClose={()=>setConfirming(false)} onConfirm={confirmInvite} busy={busy}
      events={events} email={form.email} hasAccount={hasAccount}
      prefillAssignments={selected?.detectedAssignments} prefillEventId={selected?.detectedEventId}/>

    {/* Lets the admin see/download exactly what this speaker would generate
        for themselves - same modal as SpeakerDashboard.js/AttendeeProfile.js,
        just fed this profile's own draft identity instead of the logged-in
        user's. */}
    {shareEngagement && (
      <ShareEngagementModal
        engagement={shareEngagement}
        identity={{
          name: selected.fullName, institution: selected.institution, city: selected.city, country: selected.country,
          photoUrl: selected.photoUrl,
          motivation: (intl.locale||'').startsWith('de') ? (selected.motivationDe||selected.motivationEn) : (selected.motivationEn||selected.motivationDe),
        }}
        onClose={()=>setShareEngagement(null)}
      />
    )}
  </Container>;
};

export default SpeakerDetail;
